import { useRef, useEffect } from 'react';

// Adapted original from https://usehooks.com/useEventListener/

/**
 * Hook for setInterval.
 */
export const useInterval = (
  /**
   * milliseconds for setInterval.
   */
  ms: number,
  handler: () => void,
  options: {
    /**
     * Whether the event listener should be attached or not.
     * Defaults to true.
     */
    enabled?: boolean;
  } = {}
) => {
  const { enabled = true } = options;

  // Create a ref that stores handler
  const savedHandler = useRef(handler);

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(
    () => {
      if (enabled) {
        // Create callback that calls handler function stored in ref
        const callback = () => savedHandler.current();

        // Add interval
        const interval = setInterval(callback, ms);

        // Remove interval on cleanup
        return () => {
          clearInterval(interval);
        };
      } else {
        return;
      }
    },
    [enabled, ms] // Re-run if ms changes
  );
};
